export const PROGRESS_BAR_TOTAL_STEPS = 10;
export const COLOR_MAXIMUM_ITERATION = 10;

/**
 * Inside-exploration step flow shown in the left sidebar.
 * Order matches the actual flow the executor walks through during one iteration.
 */
export const EXPLORATION_FLOW_STEPS = [
    { key: "geometric", label: "Geometric Measurement", short: "Geometric" },
    { key: "width", label: "Width Measurement", short: "Width" },
    { key: "color", label: "Color Measurement", short: "Color" },
    { key: "results", label: "Results", short: "Results" },
];

/** Last four characters of the exploration UUID string (matches backend ``exploration_id_last4``). */
export function explorationIdLast4(explorationId) {
    const s = explorationId == null ? "" : String(explorationId);
    return s.length >= 4 ? s.slice(-4) : s;
}

/**
 * Compact user-facing code for the population currently being marked or
 * measured (``"P0"`` for the initial input population, ``"Q<n>"`` for every
 * generated population). Returns ``null`` when ``phase`` is unknown.
 *
 * Use this for marking-file download names and preview titles so the lab
 * sees the same identifier as the one printed on the physical plate, instead
 * of the off-by-one internal iteration counter (e.g. internal iteration ``2``
 * maps to user-facing ``Q1``).
 */
export function populationCodeForPhase(phase, displayIterationNumber) {
    if (phase !== "P" && phase !== "Q") return null;
    const n =
        displayIterationNumber === null || displayIterationNumber === undefined
            ? null
            : Number(displayIterationNumber);
    if (phase === "P") return `P${Number.isFinite(n) ? n : 0}`;
    return `Q${Number.isFinite(n) ? n : ""}`;
}

/**
 * Human-readable label for the population currently being marked or measured
 * inside the displayed iteration. ``phase`` comes from the backend
 * ``GetCurrentIterationForExploration`` response (``"P"`` for the very first
 * input population ``P_0``, ``"Q"`` for every generated population ``Q_n``).
 *
 * Within iteration 0 the user walks through *both* P_0 (input population) and
 * Q_0 (generated population), so without this hint the sidebar's "Iteration
 * #0" looks identical for both — making it look like the executor "jumped
 * back to the start of iteration 0" when really they advanced to its second
 * half. From iteration 1 onwards only the generated population needs new
 * measurements (the input population was already measured as Q_{n-1} in the
 * previous iteration), so the label simplifies to "Generated collection".
 */
export function populationLabelForPhase(phase, displayIterationNumber) {
    const code = populationCodeForPhase(phase, displayIterationNumber);
    if (!code) return null;
    const headline =
        phase === "P" ? "Input collection" : "Generated collection";
    return `${headline} (${code})`;
}
